import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import "./style.scss";
import axios from "axios";
import AppConstants from "../../AppConstants";

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword((prev) => !prev);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const validationSchema = Yup.object({
    currentPassword: Yup.string().required("Current password is required"),
    newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Must include at least one uppercase letter")
      .matches(/[a-z]/, "Must include at least one lowercase letter")
      .matches(/[0-9]/, "Must include at least one number")
      .matches(
        /[!@#$%^&*(),.?\":{}|<>]/,
        "Must include at least one special character"
      )
      .notOneOf(
        [Yup.ref("currentPassword")],
        "New password must be different from current password"
      )
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Passwords must match")
      .required("Please confirm your new password")
  });

  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        const token = localStorage.getItem("authToken");

        const response = await axios.put(
          AppConstants.ADMIN_CHANGE_PASSWORD_API,
          {
            currentPassword: values.currentPassword,
            newPassword: values.newPassword
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (response.status === 200) {
          toast.success(
            response.data.message || "Password updated successfully!",
            {
              position: toast.POSITION.TOP_RIGHT
            }
          );

          setTimeout(() => {
            navigate("/dashboard");
          }, 1500);
        }
      } catch (error: any) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          "Failed to update password";

        toast.error(message, {
          position: toast.POSITION.TOP_RIGHT
        });
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <section className="change-password credential-page card py-5 border-0 mb-0">
      <Container fluid>
        <Row className="justify-content-md-center">
          <Col md={6} sm={12}>
            <Form onSubmit={formik.handleSubmit} noValidate>
              <Form.Group
                className="mb-3 position-relative"
                controlId="formCurrentPassword"
              >
                <Form.Label className="mb-1">
                  Current Password<sup>*</sup>
                </Form.Label>
                <Form.Control
                  type={showCurrentPassword ? "text" : "password"}
                  placeholder="Enter Current password"
                  name="currentPassword"
                  value={formik.values.currentPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="password p-3"
                  isInvalid={
                    formik.touched.currentPassword &&
                    !!formik.errors.currentPassword
                  }
                />
                <i
                  onClick={toggleCurrentPasswordVisibility}
                  className="password-icon"
                  style={{
                    position: "absolute",
                    right: "31px",
                    top: "44px",
                    cursor: "pointer"
                  }}
                >
                  {showCurrentPassword ? <BsEyeSlash /> : <BsEye />}
                </i>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.currentPassword}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group
                className="mb-3 position-relative"
                controlId="formNewPassword"
              >
                <Form.Label className="mb-1">
                  New Password<sup>*</sup>
                </Form.Label>
                <Form.Control
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  name="newPassword"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="password p-3"
                  isInvalid={
                    formik.touched.newPassword && !!formik.errors.newPassword
                  }
                />
                <i
                  onClick={toggleNewPasswordVisibility}
                  className="password-icon"
                  style={{
                    position: "absolute",
                    right: "31px",
                    top: "43px",
                    cursor: "pointer"
                  }}
                >
                  {showNewPassword ? <BsEyeSlash /> : <BsEye />}
                </i>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.newPassword}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group
                className="mb-3 position-relative"
                controlId="formConfirmPassword"
              >
                <Form.Label className="mb-1">
                  Confirm New Password<sup>*</sup>
                </Form.Label>
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  name="confirmPassword"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="password p-3"
                  isInvalid={
                    formik.touched.confirmPassword &&
                    !!formik.errors.confirmPassword
                  }
                />
                <i
                  onClick={toggleConfirmPasswordVisibility}
                  className="password-icon"
                  style={{
                    position: "absolute",
                    right: "31px",
                    top: "43px",
                    cursor: "pointer"
                  }}
                >
                  {showConfirmPassword ? <BsEyeSlash /> : <BsEye />}
                </i>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.confirmPassword}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="password-requirements mb-3">
                <p className="mb-1">Note:</p>
                <ul className="ps-3">
                  <li>Minimum length: 8 characters</li>
                  <li>
                    Include at least one uppercase letter, one lowercase letter,
                    one number, and one special character (!, @, #, etc.)
                  </li>
                  <li>Avoid common words, phrases, or predictable patterns</li>
                </ul>
              </div>

              <Button
                variant="primary"
                className="mt-2 border-0 text-white font-16 w-100 py-3 gilroy-semibold"
                type="submit"
                size="lg"
                disabled={formik.isSubmitting || loading}
              >
                {loading ? "Updating..." : "Update Password"}
              </Button>
            </Form>
          </Col>
        </Row>

        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
        />
      </Container>
    </section>
  );
};

export default ChangePassword;
